import React, { useCallback, useMemo, useState } from 'react'

import { HiChevronUpDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import { Input, TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'

import { COLOR_CLASSNAMES } from '@/constants/color'

import type { Color } from '@/types/Color'

export interface SelectOrCreateProps {
  value: string | null
  options: string[]
  onChange: (value: string | null) => void
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  allowCreate?: boolean
  optionLabels?: Record<string, string>
  optionColors?: Record<string, Color>
}

const OptionColorDot: React.FC<{
  color?: Color
  className?: string
}> = ({ color, className }) => {
  if (!color) return null

  return (
    <span
      aria-hidden={true}
      className={twMerge(
        COLOR_CLASSNAMES[color],
        'size-2 shrink-0 rounded-full border-0 p-0',
        className,
      )}
    />
  )
}

export const SelectOrCreate: React.FC<SelectOrCreateProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select or type…',
  searchPlaceholder,
  disabled = false,
  allowCreate = true,
  optionLabels,
  optionColors,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selectedColor = value ? optionColors?.[value] : undefined
  const selectedLabel = value ? (optionLabels?.[value] ?? value) : null

  const optionList = useMemo(
    () => Array.from(new Set((options ?? []).filter((o): o is string => !!o))),
    [options],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return optionList
    return optionList.filter((o) =>
      [o, optionLabels?.[o]]
        .filter(Boolean)
        .some((item) => item?.toLowerCase().includes(q)),
    )
  }, [optionLabels, optionList, query])

  const handleSelect = useCallback(
    (val: string | null) => () => {
      onChange(val)
      setOpen(false)
      setQuery('')
    },
    [onChange],
  )

  const canCreate =
    allowCreate &&
    query.trim().length > 0 &&
    !optionList.some((o) => o.toLowerCase() === query.trim().toLowerCase())

  const handleCreate = useCallback(() => {
    const v = query.trim()
    if (!v) return
    onChange(v)
    setOpen(false)
    setQuery('')
  }, [query, onChange])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
  }, [])

  const handleClear = useCallback(() => {
    onChange(null)
    setOpen(false)
    setQuery('')
  }, [onChange])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setQuery('')
    }
  }, [])

  return (
    <div>
      <Combobox open={open} onOpenChange={handleOpenChange}>
        <ComboboxTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={twMerge(
              TEXT_INPUT_CLASSNAME,
              'w-full flex items-center justify-between px-3 h-9 text-sm bg-primary-50 dark:bg-default-dark border border-primary-200 dark:border-primary-800 rounded-md hover:bg-primary-100 dark:hover:bg-default-dark/80 disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <span
              className={twMerge(
                'flex min-w-0 flex-1 text-sm',
                value
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-primary-500 dark:text-primary-400',
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <OptionColorDot color={selectedColor} />
                <span className="truncate">{selectedLabel || placeholder}</span>
              </span>
            </span>
            <HiChevronUpDown size={16} className="flex-shrink-0 ml-2" />
          </button>
        </ComboboxTrigger>
        <ComboboxContent
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="p-0 w-[320px]"
        >
          <div className="p-1">
            <Input
              type="text"
              value={query}
              onChange={handleChange}
              placeholder={
                searchPlaceholder ??
                (allowCreate
                  ? 'Search or create a new option...'
                  : 'Search options...')
              }
              className="dark:bg-default-dark placeholder:text-sm"
            />
          </div>
          <ComboboxList className="max-h-[min(300px,var(--radix-popover-content-available-height))]">
            {filtered.length === 0 && !canCreate && !value ? (
              <ComboboxEmpty className="text-sm">No matches.</ComboboxEmpty>
            ) : (
              <ComboboxGroup>
                {value && (
                  <ComboboxSelectableItem
                    key="__clear__"
                    value="__clear__"
                    isSelected={false}
                    onSelect={handleClear}
                  >
                    <span className="text-sm text-red-600 dark:text-red-400">
                      Clear selection
                    </span>
                  </ComboboxSelectableItem>
                )}
                {filtered.map((opt) => (
                  <ComboboxSelectableItem
                    key={opt}
                    value={opt}
                    isSelected={value === opt}
                    onSelect={handleSelect(opt)}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-sm">
                      <OptionColorDot color={optionColors?.[opt]} />
                      <span className="truncate">
                        {optionLabels?.[opt] ?? opt}
                      </span>
                    </span>
                  </ComboboxSelectableItem>
                ))}
                {canCreate && (
                  <ComboboxSelectableItem
                    key="__create__"
                    value={`__create__:${query}`}
                    isSelected={false}
                    onSelect={handleCreate}
                  >
                    <span className="text-sm">
                      Create &quot;{query.trim()}&quot;
                    </span>
                  </ComboboxSelectableItem>
                )}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  )
}
